import axios from 'axios';
import { config } from '@/config';

/**
 * Lectura de credenciales del admin desde localStorage.
 * Se leen frescas en cada llamada para no arrastrar un token viejo.
 *
 * @returns {{ token: string, hashTeatro: string }}
 */
function getCredentials() {
    return {
        token: localStorage.getItem('token') || '',
        hashTeatro: localStorage.getItem('hash_teatro') || '',
    };
}

/**
 * Cliente axios para la superficie **v2** de la API de eventos.
 *
 * Por qué existe: el endpoint v2 cuelga del dominio pelado (`/api/v2/events/...`),
 * NO de `.../api1`. El `baseURL` global de `api.js` es `.../api1`, así que
 * reutilizarlo daría `.../api1/api/v2/...` (404). Aquí se usa `config.API_TEATROS`
 * (el origin sin `/api1`), que es el mismo patrón que ya usa el dashboard
 * (`src/services/dashboard/dashboard.js`).
 *
 * La auth es idéntica a v1: `api_key` como query param y `Hash-Teatro` +
 * `Authorization` por cabecera. El middleware del grupo v2
 * (`checkTeatro` + `auth2`) acepta exactamente eso.
 *
 * @returns {import('axios').AxiosInstance}
 */
export function createEventsV2ApiClient() {
    const { token, hashTeatro } = getCredentials();

    const client = axios.create({
        baseURL: config.API_TEATROS,
        withCredentials: false,
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': token,
            'Hash-Teatro': hashTeatro,
        },
    });

    // api_key como query param, con el token fresco de cada request.
    client.interceptors.request.use(cfg => {
        const creds = getCredentials();
        if (creds.token) {
            cfg.params = {
                ...(cfg.params || {}),
                api_key: creds.token,
            };
        }
        return cfg;
    });

    return client;
}

/**
 * Listado paginado y filtrable de sesiones de un evento (v2).
 *
 * Endpoint: GET /api/v2/events/{eventoId}/sessions
 * Respuesta: { data: [ ...sesiones ], meta: { current_page, last_page, total, per_page } }
 *
 * Devuelve el envelope completo ({ data: { data, meta } }) para no romper el
 * contrato que ya espera `useSessionsList` (lee `res.data.data` y `res.data.meta`).
 *
 * @param {Object} params
 * @param {number|string} params.evento_id
 * @param {{ page?: number, per_page?: number, search?: string }} [params.filters]
 * @returns {Promise<{ data: { data: Array, meta: Object } }>}
 */
export async function fetchEventSessions({ evento_id, filters = {} }) {
    const client = createEventsV2ApiClient();

    const { page = 1, per_page = 10, search = '' } = filters;

    const response = await client.get(`/api/v2/events/${evento_id}/sessions`, {
        params: { page, per_page, search },
    });

    return { data: response.data };
}
